import { AdvancedFilterType } from '@/enums/AdvancedFilterType'
import { Status } from '@/enums/Status'
import { TableStoreId } from '@/enums/TableStoreId'
import type { ICustomFieldDefinition } from '@/interfaces/api/ICustomFieldDefinition'
import type { ICustomFieldsStore } from '@/interfaces/api/ICustomFieldsStore'
import type { ITableHeader } from '@/interfaces/ITableHeader'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import { type Composer } from 'vue-i18n'
import type { PeopleRecord } from '../PeopleRecord'

const _getFixedHeaders = (
  i18n: Composer<{}, {}, {}, string, never, string>
): Array<ITableHeader> => {
  const settingsStore = useSettingsStore()
  const headers: Array<ITableHeader> = [
    {
      title: i18n.t('fields.people.lastName'),
      key: 'lastName',
      align: 'start',
      nowrap: true,
      headerProps: { filterType: AdvancedFilterType.Text, searchField: true }
    },
    {
      title: i18n.t('fields.people.firstName'),
      key: 'firstName',
      align: 'start',
      nowrap: true,
      headerProps: { filterType: AdvancedFilterType.Text, searchField: true }
    },
    {
      title: i18n.t('fields.people.idNumber'),
      key: 'idNumber',
      align: 'start',
      nowrap: true,
      headerProps: { filterType: AdvancedFilterType.Text, searchField: true }
    },
    {
      title: i18n.t('fields.people.company'),
      key: 'company',
      align: 'start',
      nowrap: true,
      headerProps: { filterType: AdvancedFilterType.Text, searchField: true }
    },
    {
      title: i18n.t('fields.people.location'),
      key: 'location',
      align: 'start',
      nowrap: true,
      headerProps: { filterType: AdvancedFilterType.Text, searchField: true }
    },
    {
      title: i18n.t('fields.people.testingStatus'),
      key: 'testDueDate',
      align: 'start',
      nowrap: true,
      headerProps: {
        filterType: AdvancedFilterType.Status,
        options: [
          { title: i18n.t('labels.all'), value: null },
          { title: i18n.t('labels.compliant'), value: Status.Compliant },
          { title: i18n.t('labels.dueSoonScheduled'), value: Status.DueSoonScheduled },
          { title: i18n.t('labels.dueSoonNotScheduled'), value: Status.DueSoonNotScheduled },
          { title: i18n.t('labels.overdueScheduled'), value: Status.OverdueScheduled },
          { title: i18n.t('labels.overdueNotScheduled'), value: Status.OverdueNotScheduled },
          { title: i18n.t('labels.noPassingRecord'), value: Status.NoRecord }
        ]
      }
    },
    {
      title: i18n.t('fields.people.clearanceStatus'),
      key: 'clearanceExpiration',
      align: 'start',
      nowrap: true,
      exclude: !settingsStore.globals.clearance.enabled,
      headerProps: {
        filterType: AdvancedFilterType.Status,
        options: settingsStore.globals.clearance.expires
          ? [
              { title: i18n.t('labels.all'), value: null },
              { title: i18n.t('labels.cleared'), value: Status.Compliant },
              { title: i18n.t('labels.dueSoonScheduled'), value: Status.DueSoonScheduled },
              { title: i18n.t('labels.dueSoonNotScheduled'), value: Status.DueSoonNotScheduled },
              { title: i18n.t('labels.overdueScheduled'), value: Status.OverdueScheduled },
              { title: i18n.t('labels.overdueNotScheduled'), value: Status.OverdueNotScheduled },
              { title: i18n.t('labels.notCleared'), value: Status.NoRecord }
            ]
          : [
              { title: i18n.t('labels.all'), value: null },
              { title: i18n.t('labels.cleared'), value: Status.Compliant },
              { title: i18n.t('labels.notCleared'), value: Status.NoRecord }
            ]
      }
    },
    {
      title: i18n.t('fields.people.trainingStatus'),
      key: 'trainingExpiration',
      align: 'start',
      nowrap: true,
      exclude: !settingsStore.globals.training.enabled,
      headerProps: {
        filterType: AdvancedFilterType.Status,
        options: settingsStore.globals.training.expires
          ? [
              { title: i18n.t('labels.all'), value: null },
              { title: i18n.t('labels.compliant'), value: Status.Compliant },
              { title: i18n.t('labels.dueSoonScheduled'), value: Status.DueSoonScheduled },
              { title: i18n.t('labels.dueSoonNotScheduled'), value: Status.DueSoonNotScheduled },
              { title: i18n.t('labels.overdueScheduled'), value: Status.OverdueScheduled },
              { title: i18n.t('labels.overdueNotScheduled'), value: Status.OverdueNotScheduled },
              { title: i18n.t('labels.noRecord'), value: Status.NoRecord }
            ]
          : [
              { title: i18n.t('labels.all'), value: null },
              { title: i18n.t('labels.compliant'), value: Status.Compliant },
              { title: i18n.t('labels.noRecord'), value: Status.NoRecord }
            ]
      }
    },
    {
      title: i18n.t('fields.people.email'),
      key: 'email',
      align: 'start',
      nowrap: true,
      headerProps: { filterType: AdvancedFilterType.Text, searchField: true }
    },
    {
      title: i18n.t('fields.people.respiratorRequired'),
      key: 'respiratorRequired',
      align: 'start',
      nowrap: true,
      value: (item: PeopleRecord) =>
        `${item.respiratorRequired ? i18n.t('labels.yes') : i18n.t('labels.no')}`,
      headerProps: {
        filterType: AdvancedFilterType.List,
        options: [
          { title: i18n.t('labels.all'), value: null },
          { title: i18n.t('labels.yes'), value: 1 },
          { title: i18n.t('labels.no'), value: 0 }
        ]
      }
    },
    {
      title: i18n.t('fields.people.active'),
      key: 'inactive',
      align: 'start',
      nowrap: true,
      value: (item: PeopleRecord) =>
        `${item.inactive ? i18n.t('labels.no') : i18n.t('labels.yes')}`,
      headerProps: {
        filterType: AdvancedFilterType.List,
        options: [
          { title: i18n.t('labels.all'), value: null },
          { title: i18n.t('labels.yes'), value: 0 },
          { title: i18n.t('labels.no'), value: 1 }
        ]
      }
    },
    {
      title: i18n.t('fields.people.isSupervisor'),
      key: 'isSupervisor',
      align: 'start',
      nowrap: true,
      value: (item: PeopleRecord) =>
        `${item.isSupervisor ? i18n.t('labels.yes') : i18n.t('labels.no')}`,
      headerProps: {
        filterType: AdvancedFilterType.List,
        options: [
          { title: i18n.t('labels.all'), value: null },
          { title: i18n.t('labels.yes'), value: 1 },
          { title: i18n.t('labels.no'), value: 0 }
        ]
      }
    },
    {
      title: i18n.t('fields.people.note'),
      key: 'note',
      align: 'start',
      sortable: false,
      nowrap: true,
      headerProps: { filterType: AdvancedFilterType.Text, searchField: true }
    },
    {
      title: i18n.t('fields.actions'),
      key: 'actions',
      align: 'start',
      sortable: false,
      nowrap: true,
      headerProps: {
        filterType: AdvancedFilterType.Disabled
      }
    }
  ]
  return headers.filter((header) => {
    return !header.exclude
  })
}

const _getDynamicHeaders = async (): Promise<Array<ITableHeader>> => {
  const customFieldsStore = TableStoreFactory.get(TableStoreId.CustomFields) as ICustomFieldsStore
  await customFieldsStore.loadItems()
  const headers: Array<ITableHeader> = []
  customFieldsStore.activeFieldDefinitions.forEach((fieldDefinition: ICustomFieldDefinition) => {
    if (fieldDefinition.item?.enabled) {
      headers.push({
        title: fieldDefinition.item?.labelName || '',
        key: fieldDefinition.valueField,
        align: 'start',
        nowrap: true,
        headerProps: { filterType: AdvancedFilterType.Text, searchField: true }
      })
    }
  })
  return headers
}

export const getTableHeaders = async (
  i18n: Composer<{}, {}, {}, string, never, string>
): Promise<Array<ITableHeader>> => {
  const headers = _getFixedHeaders(i18n)
  const customHeaders = await _getDynamicHeaders()
  headers.splice(5, 0, ...customHeaders)
  return headers
}
